<template>
  <div>
    <navbar :links="links" />
    <!-- FIXME: we are missing out on the offline-decoder-selected and -select-by-association-membernumber features here -->
    <div
      :class="{
        'desktop-mode': $store.state.common.desktopMode,
        'elevation-1': scrollTop > fixedSearchThreshold,
        firefox: $store.state.common.runtimeEnv.firefox,
        safari: $store.state.common.runtimeEnv.safari,
        tools: scrollTop <= fixedSearchThreshold,
        'tools-fixed': scrollTop > fixedSearchThreshold,
        'visible-drawer': $store.state.common.showDrawer,
      }"
    >
      <v-layout
        align-center
        wrap
        class="mt-2"
      >
        <!-- Search -->
        <v-text-field
          ref="searchbar"
          v-model="searchText"
          class="searchbar-input pt-0 mt-0 ml-auto"
          single-line
          hide-details
          :label="searchLabel"
          clearable
          append-icon="search"
          @click:append="focusSearch()"
          @click:clear="clearSearch()"
          @keyup.native.esc="clearSearch()"
        />
        <!-- Include menu players checkbox -->
        <v-slide-x-transition>
          <v-checkbox
            v-if="
              !!$userprofile &&
                !!$userprofile.workspace &&
                $userprofile.workspace.has_menus
            "
            v-model="includeMenuPlayers"
            color="msr"
            class="ml-4"
            style="flex-grow: 0"
            :label="$t('addDecoderToSpotIncludeMenuPlayers')"
            prepend-icon="restaurant"
          />
        </v-slide-x-transition>
        <!-- Toggle all-selected checkbox -->
        <div
          class="ml-auto"
          :class="{
            'mr-5':
              $vuetify.breakpoint.smAndUp && scrollTop > fixedSearchThreshold,
          }"
        >
          <v-checkbox
            v-model="allSelected"
            class="msr--text ml-3"
            color="msr"
            hide-details
          />
        </div>
      </v-layout>
    </div>
    <!-- Fixed spacer for tools widget -->
    <div
      v-if="scrollTop > fixedSearchThreshold"
      class="tools-fixed-spacer"
    />
    <!-- Content Selection Info -->
    <content-selection-info
      :selected-contents="selectedContents"
      :show-compatible-content-explanation="showCompatibleContentExplanation"
    />
    <!-- No available Decoders warning -->
    <v-list v-if="!loading && decoders && !filteredStoreDecoderWrappers.length">
      <v-subheader>{{ $t('noAvailableDecoders') }}</v-subheader>
    </v-list>
    <!-- Decoder list -->
    <selectable-store-decoders
      v-for="(wrapper, wrapperIndex) in filteredStoreDecoderWrappers"
      :key="wrapper.store.id"
      ref="selectableStoreDecoders"
      :store="wrapper.store"
      :decoders="wrapper.decoders"
      :preselect="decoders.length === 1"
      :num-target-contents="selectedContents.length"
      :show-divider="wrapperIndex !== filteredStoreDecoderWrappers.length - 1"
    />
    <!-- Empty placeholder to allow a bit more scrolling -->
    <div style="height: 100px" />
    <!-- Add selected Decoders button -->
    <v-slide-y-reverse-transition>
      <v-btn
        v-if="atLeastOneDecoderSelected && !showConfirmationDialog"
        dark
        round
        fixed
        bottom
        right
        data-cy="continue-btn"
        :class="$desktopModeStyles"
        class="msr z-index-1"
        @click.stop="showConfirmationDialog = true"
      >
        <v-icon left>
          arrow_forward
        </v-icon>
        {{ $t('goForward') }}
      </v-btn>
    </v-slide-y-reverse-transition>
    <!-- Loading Indicator -->
    <div class="loading-indicator">
      <v-progress-circular
        v-if="loading"
        :size="50"
        indeterminate
        class="mt-5 msr--text"
      />
    </div>
    <!-- Confirmation Dialog -->
    <entry-creation-dialog
      ref="confirmationDialog"
      :show.sync="showConfirmationDialog"
      :title="dialogTitle"
      :contents="selectedContents"
      :busy="adding"
      :allow-fullscreen="!onlyTickersSelected"
      :show-spot-options="!onlyTickersSelected"
      :show-weekday-options="!onlyTickersSelected"
      :show-hotrotation="!onlyClosingTimeRadioSpotsSelected"
      :show-date-controls="!onlyClosingTimeRadioSpotsSelected"
      :show-timeframe-controls="!onlyClosingTimeRadioSpotsSelected"
      @aborted="handleDialogAbortion()"
      @confirmed="handleDialogConfirmation"
    />
    <!-- Content Activation Warnings Dialog -->
    <content-activation-warnings-dialog
      :show.sync="showContentActivationWarningsDialog"
      :contents="selectedContents"
      :warnings="contentActivationWarnings"
      @close="resetAndNavigateToContentsList()"
    />
  </div>
</template>

<script>
import addDecoderTo from '@/mixins/addDecoderTo.js';
import identifiers from '@/mixins/identifiers.js';
import trackScrollTop from '@/mixins/trackScrollTop.js';

import ContentActivationWarningsDialog from '@/components/ContentActivationWarningsDialog';
import ContentSelectionInfo from '@/components/ContentSelectionInfo';
import EntryCreationDialog from '@/components/EntryCreationDialog';
import Navbar from '@/components/Navbar';
import SelectableStoreDecoders from '@/components/SelectableStoreDecoders';

export default {
  components: {
    ContentActivationWarningsDialog,
    ContentSelectionInfo,
    EntryCreationDialog,
    Navbar,
    SelectableStoreDecoders,
  },
  mixins: [addDecoderTo, identifiers, trackScrollTop],
  data() {
    return {
      decoders: [],
      showContentActivationWarningsDialog: false,
      contentActivationWarnings: [],
    };
  },
  computed: {
    includeMenuPlayers: {
      get() {
        return this.$store.state.common.includeMenuPlayers;
      },
      set(includeMenuPlayers) {
        this.$store.commit('SET_INCLUDE_MENU_PLAYERS', includeMenuPlayers);
      },
    },
    onlyClosingTimeRadioSpotsSelected() {
      if (this.selectedContents.length === 0) {
        return false;
      }
      for (let content of this.selectedContents) {
        if (!this.isClosingTimeRadioSpot(content)) {
          return false;
        }
      }
      return true;
    },
    onlyTickersSelected() {
      if (this.selectedContents.length === 0) {
        return false;
      }
      for (let content of this.selectedContents) {
        if (!this.isTicker(content)) {
          return false;
        }
      }
      return true;
    },
    /** Only show when there are Decoders that don't support all target contents. */
    showCompatibleContentExplanation() {
      const numTargetContents = this.selectedContents.length;
      for (let decoder of this.decoders) {
        const num = decoder.num_compatible_target_contents || 0;
        if (num < numTargetContents) {
          return true;
        }
      }
      return false;
    },
    links() {
      const num = this.selectedContents.length;
      const numLabel =
        num === 1
          ? this.$t('activateMultipleContentsTogetherNumSingular')
          : this.$t('activateMultipleContentsTogetherNum').replace(
              '{{ num }}',
              num
            );
      return ['contents', {label: numLabel}];
    },
    selectedContents() {
      return this.$store.state.common.selectedContentsForBatchActivation;
    },
  },
  watch: {
    includeMenuPlayers: function () {
      this.resetDecoderSelection();
      this.fetchUserDecodersMixed();
    },
  },
  mounted() {
    this.fetchUserDecodersMixed();
  },
  methods: {
    fetchUserDecodersMixed() {
      this.loading = true;
      this.$api
        .listUserDecodersMixed(this.selectedContents, this.includeMenuPlayers)
        .then((response) => {
          this.decoders = response.data;
          this.loading = false;
        });
    },
    handleDialogConfirmation(additionalData) {
      const selectedDecoders = this.getSelectedDecoders();

      const dialogOptions = this.$refs.confirmationDialog.options;
      const startDate = dialogOptions.expandDateControls
        ? this.$refs.confirmationDialog.startDate
        : null;
      const stopDate = dialogOptions.expandDateControls
        ? this.$refs.confirmationDialog.stopDate
        : null;

      const minutesBeforeClosing =
        this.$refs.confirmationDialog.minutesBeforeClosing;
      const weekDays = this.$refs.confirmationDialog.weekDays;

      const hotrotation = dialogOptions.hotrotation;
      const spotIdToOverlayData = additionalData.spotIdToOverlayData;

      const opts = {
        closingtime_minutes_before_closing: minutesBeforeClosing,
        hotrotation: hotrotation,
        spotIdToOverlayData: spotIdToOverlayData,
        startDate: startDate,
        stopDate: stopDate,
        weekdays: weekDays,
      };

      if (
        additionalData.timeframeIds &&
        additionalData.timeframeIds.length > 0
      ) {
        opts.timeframeIds = additionalData.timeframeIds;
      }

      this.activateSelectedContents(
        this.selectedContents,
        selectedDecoders,
        opts
      );
    },
    resetAndNavigateToContentsList() {
      this.$store.commit('UNSET_PRE_ROUTE_QUESTION');
      this.$store.commit('SET_FORCE_REFETCH_CONTENTS_ON_NEXT_VISIT', true);
      this.$router.push({path: '/content'});
    },
    /**
     * Activate mixed contents on mixed decoders with options.
     *
     * Args:
     *   selectedContents (Array[Object])
     *
     *   selectedDecoders (Array[Object])
     *
     *   opts (Object):
     *     - startDate (String): Optional
     *     - stopDate (String): Optional
     *     - hotrotation (Boolean): Optional
     *     - timeframeId (Number): Optional
     *     - overlayData (Object): Optional
     */
    activateSelectedContents(selectedContents, selectedDecoders, opts) {
      this.adding = true;
      this.$api
        .activateSelectedContents(selectedContents, selectedDecoders, opts)
        .then((response) => {
          // Display success toast.
          this.showConfirmationDialog = false;
          this.$bus.$emit('alert-success', {
            message: 'activateMultipleContentsTogetherSuccess',
            translate: true,
            timeout: 5000,
          });

          // Display any warnings we may have received.
          const warnings = response.data.warnings;
          if (warnings.length > 0) {
            this.contentActivationWarnings = warnings;
            this.showContentActivationWarningsDialog = true;
          } else {
            this.resetAndNavigateToContentsList();
          }
        })
        .catch((error) => {
          this.$bus.$emit('alert-error', {
            message: 'genericFailedInfo',
            translate: true,
          });
        })
        .finally(() => {
          this.adding = false;
        });
    },
  },
};
</script>

<style src="@/mixins/addDecoderTo.global.css"></style>

<style scoped src="@/mixins/addDecoderTo.scoped.css"></style>



// WEBPACK FOOTER //
// src/pages/AddDecoderToContentSelection.vue